import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect } from "react";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { MobileNav } from "./components/MobileNav";
import { Player } from "./components/Player";
import { RightSidebar } from "./components/RightSidebar";
import { Sidebar } from "./components/Sidebar";
import { Toaster } from "./components/ui/toaster";

import { DocsHomePage } from "./components/docs/DocsHomePage";
import { DocsLayout } from "./components/docs/DocsLayout";
import { HapticFeedbackPage } from "./components/docs/HapticFeedbackPage";
import { InstallationPage } from "./components/docs/InstallationPage";
import { IntroductionPage } from "./components/docs/IntroductionPage";
import { ShortcutsPage } from "./components/docs/ShortcutsPage";
import { TroubleshootingPage } from "./components/docs/TroubleshootingPage";
import { AlbumDetails } from "./pages/AlbumDetails";
import { Albums } from "./pages/Albums";
import BlogIndexPage from "./pages/blog/BlogIndexPage"; // Step 7: Import BlogIndexPage
import BlogPostPage from "./pages/blog/BlogPostPage"; // Step 7: Import BlogPostPage
import { Debug } from "./pages/Debug";
import { Help } from "./pages/Help";
import { Home } from "./pages/Home";
import { Library } from "./pages/Library";
import { LikedSongs } from "./pages/LikedSongs";
import { LocalFiles } from "./pages/LocalFiles";
import { PlaylistDetails } from "./pages/PlaylistDetails";
import { Playlists } from "./pages/Playlists";
import { Privacy } from "./pages/Privacy";
import { Profile } from "./pages/Profile";
import { Radio } from "./pages/Radio";
import { RadioStationDetails } from "./pages/RadioStationDetails";
import { RecentlyPlayed } from "./pages/RecentlyPlayed";
import { Search } from "./pages/Search";
import { Settings } from "./pages/Settings";
import { Statistics } from "./pages/Statistics";
import { Test } from "./pages/Test";
import { TrackDetails } from "./pages/TrackDetails";
import { AudioContextProvider } from "./providers/AudioContextProvider";
import { ShortcutsProvider } from "./providers/ShortcutsProvider";
import { setupGlobalErrorHandling } from "./stores/errorStore";
import "./styles/player.css";

const queryClient = new QueryClient();
export const outerMargin = "7px";

function App() {
  // Removed useInitializeDemoTracks() - demo tracks are now handled by localLibraryStore
  // useInitializeDemoTracks();

  // Setup global error handling
  useEffect(() => {
    setupGlobalErrorHandling();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AudioContextProvider>
        <ShortcutsProvider>
          <Router>
            <div
              style={{ "--outer-margin": outerMargin } as React.CSSProperties}
              className="flex h-screen bg-zinc-900 text-white overflow-hidden"
            >
              <Sidebar />
              <main className="flex-1 pb-20 md:pb-[60px] overflow-y-auto bg-gradient-to-b from-zinc-900 to-black md:my-[--outer-margin] md:mr-[--outer-margin] overflow-hidden rounded-lg h-[calc(100%-14px)]">
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/search" element={<Search />} />
                  <Route path="/library" element={<Library />} />
                  <Route path="/playlists" element={<Playlists />} />
                  <Route path="/playlists/:id" element={<PlaylistDetails />} />
                  <Route path="/albums" element={<Albums />} />
                  <Route path="/albums/:id" element={<AlbumDetails />} />
                  <Route path="/tracks/:id" element={<TrackDetails />} />
                  <Route path="/liked" element={<LikedSongs />} />
                  <Route path="/recent" element={<RecentlyPlayed />} />
                  <Route path="/local" element={<LocalFiles />} />
                  <Route path="/radio" element={<Radio />} />
                  <Route path="/radio/:id" element={<RadioStationDetails />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/help" element={<Help />} />
                  <Route path="/statistics" element={<Statistics />} />
                  <Route path="/test" element={<Test />} />
                  <Route path="/debug" element={<Debug />} />
                  <Route path="/docs" element={<DocsLayout />}>
                    <Route index element={<DocsHomePage />} />
                    <Route path="getting-started/introduction" element={<IntroductionPage />} />
                    <Route path="getting-started/installation" element={<InstallationPage />} />
                    <Route path="features/shortcuts" element={<ShortcutsPage />} />
                    <Route path="features/haptic-feedback" element={<HapticFeedbackPage />} />
                    <Route path="troubleshooting" element={<TroubleshootingPage />} />
                  </Route>
                  {/* Step 7: Add Blog Routes */}
                  <Route path="/blog" element={<BlogIndexPage />} />
                  <Route path="/blog/:slug" element={<BlogPostPage />} />
                  <Route path="*" element={<Home />} />
                </Routes>
              </main>
              <Player />
              <MobileNav />
              <RightSidebar />
            </div>
          </Router>
        </ShortcutsProvider>
      </AudioContextProvider>
      <Toaster />
    </QueryClientProvider>
  );
}

export default App;
